'use client';

import { ChangeEvent, FormEvent, useState } from "react";
import clsx from "clsx";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

import { AnimateOnView } from "@/components/ui/AnimateOnView";

export default function HeroSearch() {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Construir URL de busca com query params
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set('q', query.trim());
    }

    // Navegar para página de busca
    const searchUrl = params.toString() ? `/busca?${params.toString()}` : '/busca';
    router.push(searchUrl as any);
  };

  return (
    <section id="hero" className="relative overflow-hidden rounded-[48px]">
      <div className="absolute inset-0">
        {/* Gradiente removido - hero ficará com fundo transparente */}

        {/* left decorative icons removed per request */}

        {/* decorative blob removed per request */}
      </div>

      {/* Container com altura igual à imagem do guaxinim */}
      <div className="relative z-10 mx-auto flex max-w-7xl flex-col px-6 sm:px-10 lg:px-16">
        {/* Textos sobrepostos acima do card */}
        <div className="mb-8 flex flex-col items-center gap-4 text-center sm:mb-12">
          <AnimateOnView as="span" delay={100} className="rounded-full bg-brand-secondary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.45em] text-brand-secondary backdrop-blur-sm border border-brand-secondary/20">
            Inspiração ilimitada
          </AnimateOnView>

          <AnimateOnView
            as="h1"
            delay={400}
            className="text-balance text-4xl font-bold leading-tight text-white drop-shadow-lg sm:text-5xl lg:text-6xl"
          >
            O que vamos{" "}
            <span className="relative inline-block bg-brand-gradient bg-clip-text text-transparent">
              criar
              <svg
                aria-hidden="true"
                viewBox="0 0 210 30"
                className="absolute left-0 top-[42px] h-[0.30em] w-full fill-brand-secondary sm:top-[57px]"
                preserveAspectRatio="none"
              >
                <path
                  d="M203.371.916c-26.013-2.078-76.686 1.963-124.73 9.946L67.3 12.749C35.421 18.062 18.2 21.766 6.004 25.934 1.244 27.561.828 27.778.874 28.61c.07 1.214.828 1.121 9.595-1.176 9.072-2.377 17.15-3.92 39.246-7.496C123.565 7.986 157.869 4.492 195.942 5.046"
                ></path>
              </svg>
            </span>
            {" "}hoje?
          </AnimateOnView>

          <AnimateOnView as="p" delay={700} className="max-w-2xl text-base text-slate-300 drop-shadow-md sm:text-lg">
            Busque coleções exclusivas, assets em alta e ferramentas inteligentes. Tudo pensado para acelerar seu fluxo criativo.
          </AnimateOnView>
        </div>

        {/* Barra de busca minimalista */}
        <AnimateOnView
          as="form"
          delay={1000}
          animation="animate__fadeInUp"
          onSubmit={handleSubmit}
          className={clsx("collections-search-container", "mx-auto w-full max-w-3xl")}
          aria-label="Pesquisar conteúdos"
          suppressHydrationWarning
        >
          {/* Barra de busca */}
          <div
            className={clsx(
              "collections-search-bar",
              "relative flex items-center gap-3 rounded-2xl border bg-brand-surface/30 px-5 py-3 shadow-lg backdrop-blur-xl transition-all ring-0 focus-within:ring-0",
              isFocused
                ? "border-white/20 bg-brand-surface/40"
                : "border-white/10 hover:border-white/20 hover:bg-brand-surface/40"
            )}
          >
            {/* Icon */}
            <FontAwesomeIcon
              icon={faSearch}
              className="h-5 w-5 text-white/40 transition-colors"
            />

            {/* Input */}
            <input
              type="search"
              value={query}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Pesquise aqui..."
              className="flex-1 bg-transparent text-base text-white placeholder-white/40 outline-none focus:ring-0 focus:outline-none focus-visible:outline-none"
              aria-label="Pesquisar conteúdos"
              suppressHydrationWarning
            />

            {/* Clear button (visible when query present) */}
            {query.trim().length > 0 && (
              <button
                type="button"
                onClick={() => { setQuery(''); }}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/60 transition-all hover:bg-white/20 hover:text-white mr-2"
                aria-label="Limpar busca"
              >
                {/* reuse icon */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}

            {/* Submit button */}
            <button
              type="submit"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-white shadow-lg transition-transform hover:scale-110 active:scale-95"
              aria-label="Pesquisar"
            >
              <FontAwesomeIcon icon={faSearch} className="h-4 w-4" />
            </button>
          </div>
        </AnimateOnView>
      </div>
    </section>
  );
}
